import cx from 'classnames'
import isEmpty from 'lodash.isempty'
import PubSub from 'pubsub-js'
import React, { useEffect, useState } from 'react'

import { get, patch, post } from '@js/common/api'
import Geocoder from '@js/common/geocoder'
import { trackingEvent } from '@js/common/tracking-utils'
import { UA } from '@js/common/UA'
import User from '@js/common/user'
import { getUserSessionData } from '@js/common/userSessionData'
import {
  getUUIDv4,
  isHouseRelatedType,
  isVendorRelatedType
} from '@js/common/utils'

import CTA from '@f/elements/CTA'
import Icon from '@f/elements/Icon'

import {
  computeHeadingTitle,
  computePersonalInfoPayload,
  getFlowTypeByRequestType,
  getInitialWebTrackingValueByRequestType,
  getWizardRelevantUserData,
  getWizardStepsByRequestType
} from '@f-fertighaus/applications/RequestWizard/partials/utils'
import { WIZARD_REQUEST_TYPES } from '@f-fertighaus/applications/RequestWizard/partials/wizardConstants'

import { HouseImage } from './partials/HouseImage'
import {
  getHardRequestConfig,
  getSelectedInstancesIds,
  getWizardConfigByRequestType,
  mapHouseOfInterestParams
} from './partials/requestWizardUtils'
import SendRequest from './steps/SendRequest'

const conversionFunnelWithDefaultValuesForConversion = [
  'house-wizard-item-priceinfo',
  'house-wizard-item-infomaterial',
  'house-wizard-item-configuration',
  'house-wizard-item-offer',
  'house-wizard-item-comparison',
  'house-wizard-item-catalog',
  'house-wizard-item-appointment'
]

const updateTrackingLabel = (funnelVariant = '') =>
  funnelVariant.includes('house-wizard-item')
    ? 'house-wizard-item'
    : funnelVariant

const RequestWizard = ({
  houses = [],
  vendors = [],
  outfitters = [],
  ...props
}) => {
  const [state, setState] = useState({
    flow: {
      loading: true,
      progressIsComplete: false,
      progressIsHidden: false,
      canGoBack: false,
      introType: props.introType,
      step: 1,
      steps: getWizardStepsByRequestType(
        props.requestType,
        props.funnelVariant
      ),
      trackingPathway: []
    },

    data: {
      user: getWizardRelevantUserData(),
      location: User.getLocation(),
      trackingInfo: {
        flow_information: {
          flow_type:
            updateTrackingLabel(props.funnelVariant) ||
            getFlowTypeByRequestType(props.requestType),
          flow_id: getUUIDv4(),
          flow_kickoff: houses.length > 0 ? houses : vendors,
          flow_step: 1
        }
      },
      _wtValue: getInitialWebTrackingValueByRequestType(props.requestType)
    }
  })
  const [userAlreadyExists, setUserAlreadyExists] = useState(false)
  const { flow, data } = state

  const updateData = propsToBeChanged => {
    setState(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        ...propsToBeChanged
      }
    }))
  }

  const updateFlow = propsToBeChanged => {
    setState(prevState => ({
      ...prevState,
      flow: {
        ...prevState.flow,
        ...propsToBeChanged
      }
    }))
  }

  const hideProgressBar = () => {
    updateFlow({ progressIsHidden: true })
  }

  const showProgressBar = () => {
    updateFlow({ progressIsHidden: false })
  }

  const completeProgressBar = () => {
    updateFlow({ progressIsComplete: true, progressIsError: false })
  }

  const errorProgressBar = () => {
    updateFlow({ progressIsHidden: false, progressIsError: true })
  }

  const updateLocation = newLocation => {
    if (newLocation) {
      updateData({ location: newLocation })
      User.setLocation(newLocation)
    }
  }

  const handleUserDataChange = ({ detail, forcePostRequest }) => {
    const fieldsToSave = {}

    const filterChangedFieldsOnly = () => {
      for (let field in detail) {
        if (
          !state.data.user[field] ||
          state.data.user[field] !== detail[field]
        ) {
          fieldsToSave[field] = detail[field]
        }
      }
    }

    filterChangedFieldsOnly()

    const fieldsObjectNotEmpty = Object.keys(fieldsToSave).length > 0
    if (fieldsObjectNotEmpty) {
      updateData({
        user: {
          ...state.data.user,
          ...fieldsToSave
        }
      })
    }

    if (
      (detail.email && props.requestType !== WIZARD_REQUEST_TYPES.outfitter) ||
      forcePostRequest
    ) {
      postRequest({
        payload: detail
      })
    }
  }

  const updateProfile = (payload, email) => {
    const userLS = JSON.parse(localStorage.getItem('user') || '{}')
    const deliveryRegionCity = userLS?.project?.delivery_region?.city
    const deliveryRegionState =
      userLS?.project?.delivery_region?.state?.long_name

    post('profile', {
      ...payload,
      email,
      delivery_region_city_name: deliveryRegionCity || undefined,
      delivery_region_state: deliveryRegionState || undefined
    }).then(response => {
      localStorage.setItem('wt_pid', response.public_id)
      updateData({ _wtIndicates: response.indicates })

      if (response?.JWT?.access_token && response?.JWT?.expires) {
        const trackingEventLoginData = isConversionFunnel
          ? {
              category: 'login',
              action: 'success',
              label: 'user-account',
              location: props.funnelVariant,
              values: [{ type: 'account-creation', value: 'VALUE-HIDDEN' }]
            }
          : {
              action: 'success',
              label: 'user-account',
              value: 'account-creation',
              location: 'wizard'
            }

        trackingEvent.login(trackingEventLoginData)
        PubSub.publish('USER_LOGGED_IN', {})
        UA.doLogIn({
          token: response.JWT.access_token,
          expires: response.JWT.expires,
          public_id: response.public_id
        })
      } else if (isEmpty(response.JWT)) {
        setUserAlreadyExists(true)
      }
    })
  }

  const postRequest = (options = {}) => {
    const prequal = state.data.is_prequal
    const isHouseRequestType = isHouseRelatedType(props.requestType)
    const isVendorRequestType = isVendorRelatedType(props.requestType)

    const wizardRequestConfig = getWizardConfigByRequestType(props.requestType)

    const selectedInstances =
      state.data.selected_instances[wizardRequestConfig.selectedInstancesKey]

    const selectedInstancesIds = selectedInstances
      ? getSelectedInstancesIds(selectedInstances)
      : []
    const funnel = wizardRequestConfig.funnelValue
    const selectedInstanceClass = wizardRequestConfig.selectedInstanceClass

    const updatedPrequal =
      !prequal && (isHouseRequestType || isVendorRequestType)
        ? !!selectedInstances.find(vendor => vendor.is_prequal)
        : prequal

    const additional_payload = /fu/.test(
      window['🍪'].read('cookie_policy_config')
    )
      ? JSON.parse(localStorage.getItem('leads') || '{}').additional_payload
      : {}

    const hardRequestConfig = options?.hardRequest?.vendors
      ? getHardRequestConfig(updatedPrequal, options.hardRequest.vendors)
      : {}

    const houseOfInterest = data.selected_instances.houses?.[0]

    const houseOfInterestParams =
      isHouseRelatedType && houseOfInterest
        ? mapHouseOfInterestParams(houseOfInterest)
        : {}

    const userToSave = { ...state.data.user, wishes: '' }
    User.storeUserDetails(userToSave)
    if (isHouseRequestType) {
      const vendorNid = state.data.selected_instances.vendors[0]?.nid
      const vendorInstances = selectedInstances.filter(instance =>
        isVendorRelatedType(instance)
      )
      const vendorNids = vendorInstances.map(instance => instance.nid)
      const requestedVendors = [vendorNid, ...vendorNids]
      requestedVendors.forEach(User.storeRequestedVendor)
    }

    let payload = {
      ...state.data.user,
      ...options.payload,
      property_delivery_region: state.data.location.postcode,
      type: props.requestType,
      additional_payload: {
        ...additional_payload,
        ...state.data.trackingInfo
      },
      selected_instances: selectedInstancesIds,
      selected_instance_class: selectedInstanceClass,
      funnel,
      favourites: UA.getLS().ua_favourites,
      ...hardRequestConfig,
      ...houseOfInterestParams
    }

    const location = User.getLocation()
    if (location.city && location.state) {
      payload.ua_delivery_region_verbose = location
    }

    if (!payload.property_delivery_region) {
      delete payload.property_delivery_region
    }

    if (payload.wishes === '' || options.hardRequest) {
      delete payload.wishes
    }

    if (
      state.data.uuid &&
      !options.hardRequest &&
      props.requestType !== WIZARD_REQUEST_TYPES.outfitter
    ) {
      enrichLead(payload)
    } else if (options.payload?.street || options.hardRequest) {
      const payloadWithDefaultValuesForConversion = {
        ...payload,
        construction_method: payload.construction_method || [
          'prefab',
          'solid',
          'blockhouse'
        ],
        construction_start: payload.construction_start || 'within_1_to_2_years',
        property: payload.property || 'no'
      }

      const catalogRequestPayload = conversionFunnelWithDefaultValuesForConversion.includes(
        props.funnelVariant
      )
        ? payloadWithDefaultValuesForConversion
        : payload

      catalogRequest(catalogRequestPayload, prequal)
    }

    const email =
      state.data?.user?.email || options.payload?.email || UA.getLS().email
    if (email && !options.payload?.street) {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        selected_instances: unusedSelectedInstances,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        selected_instance_class: unusedSelectedInstanceClass,
        ...payloadWithoutHandoverConsent
      } = payload
      updateProfile(payloadWithoutHandoverConsent, email)
    }
  }

  const catalogRequest = async (payload, prequal) => {
    let finalPayload = payload
    let uaData = UA.getLS()

    if (props.requestType === WIZARD_REQUEST_TYPES.houseDetailForm) {
      const personalInfoPayload = await computePersonalInfoPayload(
        payload,
        uaData
      )
      finalPayload = {
        ...finalPayload,
        ...personalInfoPayload
      }
    }
    if (props.requestType !== WIZARD_REQUEST_TYPES.outfitter) {
      finalPayload = {
        ...finalPayload,
        vendor_handover_consent: true
      }
    }
    const userSessionData = getUserSessionData()

    post('catalog/request', {
      ...finalPayload,
      session_data: userSessionData
    }).then(response => {
      let uuid = response.uuid

      updateData({
        uuid,
        prequal
      })
    })
  }

  const enrichLead = payload => {
    if (state.data.uuid) {
      let userToSave = { ...state.data.user, wishes: '' }
      User.storeUserDetails(userToSave)

      patch('leads/payload/' + state.data.uuid, payload)
    }
  }

  const move = ({ stepNumber = 1, Step = undefined }) => {
    const newStep = Step
      ? steps.findIndex(Step) - state.flow.step
      : state.flow.step + stepNumber

    if (newStep <= state.flow.steps.length && newStep > 0) {
      setState(prevState => ({
        data: {
          ...prevState.data,
          trackingInfo: {
            ...prevState.data.trackingInfo,
            flow_information: {
              ...prevState.data.trackingInfo.flow_information,
              flow_step: newStep
            }
          }
        },
        flow: {
          ...prevState.flow,
          step: newStep,
          canGoBack:
            newStep > 1 && prevState.flow.steps[newStep - 1] !== SendRequest,
          progressIsComplete: prevState.flow.steps[newStep - 1] === SendRequest
        }
      }))
    }
  }

  const getTrackingStep = step => {
    // If user has not seen this step, add step to 'pathway'
    if (!state.flow.trackingPathway.includes(step)) {
      state.flow.trackingPathway.push(step)
    }
    // Return position of step within 'pathway'
    return state.flow.trackingPathway.indexOf(step) + 1
  }

  const closeOverlay = () => {
    if (state.data._wtValue) {
      _wt({
        category: 'modal',
        action: 'hide',
        values: [
          {
            type: 'name',
            value: state.data._wtValue
          }
        ],
        ...state.data.trackingInfo
      })
    }

    props.closeModal()
  }

  const updateTrackingInfo = (trackingInfo = {}) => {
    updateData({
      trackingInfo: {
        flow_information: {
          ...state.data.trackingInfo.flow_information,
          ...trackingInfo
        }
      }
    })
  }

  const update_wtValue = ({ value, skipTracking = false }) => {
    if (value && state.data._wtValue !== value && !skipTracking) {
      _wt({
        category: 'modal',
        action: 'display',
        values: [
          {
            type: 'name',
            value
          }
        ],
        ...state.data.trackingInfo
      })
      updateData({ _wtValue: value })
    }
  }

  const setInitialLocation = () => {
    if (
      !data.location.postcode &&
      data.user.country &&
      /AT|DE/.test(data.user.country) &&
      data.user.postal_code
    ) {
      let geocoder = new Geocoder()
      geocoder
        .getLocationFromPostcode(
          `${data.user.country}-${data.user.postal_code}`
        )
        .then(
          location => {
            updateLocation(location)
          },
          () => {}
        )
    }
  }

  const triggerMountingTrackingEvent = () => {
    if (!isHouseRelatedType(props.requestType)) {
      _wt({
        category: 'modal',
        action: 'display',
        values: [
          {
            type: 'name',
            value: data._wtValue
          }
        ],
        ...data.trackingInfo
      })
    }
  }

  const fetchData = () => {
    const loadAPIResults = (type, results, skipLoading = false) => {
      setState(prevState => ({
        data: {
          ...prevState.data,
          selected_instances: {
            ...prevState.data.selected_instances,
            [type]: results
          }
        },
        flow: {
          ...flow,
          loading: skipLoading
        }
      }))
    }

    if (outfitters.length > 0) {
      get('outfitters', { nid__in: outfitters.join() }).then(data => {
        if (data.results) loadAPIResults('outfitters', data.results)
      })
    } else if (vendors.length > 0) {
      get('vendors', { nid__in: vendors.join() }).then(data => {
        if (data.results) loadAPIResults('vendors', data.results)
      })
    } else if (houses.length > 0) {
      get('houses', { nid__in: houses.join() }).then(data => {
        if (data.results) {
          const houseResults = data.results
          get('vendors', {
            nid__in: `${houseResults[0]?.vendor_verbose.nid}`
          }).then(data => {
            if (data.results) loadAPIResults('vendors', data.results, true)

            loadAPIResults('houses', houseResults)
          })
        }
      })
    }
  }

  useEffect(setInitialLocation, [])
  useEffect(triggerMountingTrackingEvent, [])
  useEffect(fetchData, [])

  const {
    loading,
    step,
    steps,
    canGoBack,
    progressIsHidden,
    progressIsComplete,
    progressIsError
  } = flow
  const { selected_instances } = data
  const loadingPercentage = (100 / steps.length) * step

  if (loading) {
    return (
      <div className='loading-indicator' style={{ transform: 'scale(0.5)' }} />
    )
  }

  const headingTitle = computeHeadingTitle(
    props.requestType,
    selected_instances.vendors?.[0]?.display_name ||
      selected_instances.vendors?.[0]?.name
  )
  const headingSubtitleText =
    props.requestType === WIZARD_REQUEST_TYPES.outfitter &&
    `${selected_instances.outfitters?.length} Hersteller ausgewählt`
  const headingSubtitle = (
    <span data-sel='txt-numitm'>{headingSubtitleText}</span>
  )

  const propsForChildren = {
    requestType: props.requestType,
    funnelVariant: props.funnelVariant,
    userAlreadyExists,
    flow: {
      ...flow,
      headingTitle,
      headingSubtitle
    },
    data,
    actions: {
      move,
      getTrackingStep,
      updateTrackingInfo,
      updateData,
      postRequest,
      enrichLead,
      updateLocation,
      hideProgressBar,
      showProgressBar,
      completeProgressBar,
      errorProgressBar,
      handleUserDataChange,
      update_wtValue,
      closeOverlay
    }
  }

  const CurrentStep = steps[step - 1]

  const isConversionFunnel = Boolean(props.funnelVariant)

  const isSendRequestStep = steps[step - 1].name === 'SendRequest'

  return (
    <div
      className={cx(
        'RequestWizard-wrapper',
        isConversionFunnel && 'RequestWizard-wrapper--HDP_conversion-funnel'
      )}>
      <div
        className={cx(
          !isSendRequestStep && isConversionFunnel
            ? 'RequestWizard--HDP_conversion-funnel'
            : 'RequestWizard'
        )}>
        <>
          {!isSendRequestStep && isConversionFunnel && <HouseImage />}
          {step <= steps.length && <CurrentStep {...propsForChildren} />}
        </>
      </div>
      {!progressIsHidden && !isConversionFunnel && (
        <div
          className={`${
            progressIsError ? 'f-c-ba-err' : 'f-c-ba-suc400'
          } RequestWizard-progress-bar`}
          style={{
            width: `${progressIsComplete ? 100 : loadingPercentage}%`
          }}
        />
      )}
      <CTA
        type='icon'
        tag='button'
        className={cx(
          'Modal-top-button Modal-top-button-close',
          isConversionFunnel &&
            'RequestWizard__modal-close-button--HDP_conversion-funnel'
        )}
        onClick={closeOverlay}
        data-sel='btn-cls'
        wtData={{
          'CTA-click': {
            label: 'close',
            location: `modal-${state.data._wtValue}`,
            ...state.data.trackingInfo
          }
        }}>
        <Icon icon='close-16px' className='[ f-icon-030 f-c-st-bla ]' />
      </CTA>
      {canGoBack && (
        <CTA
          type='icon'
          className='Modal-top-button Modal-top-button-back'
          onClick={() =>
            move({
              stepNumber: -1
            })
          }
          data-sel='btn-back'
          wtData={{
            'CTA-click': {
              label: 'back',
              location: `modal-${state.data._wtValue}`,
              ...state.data.trackingInfo
            }
          }}>
          <Icon
            icon='left-arrow-16px'
            className='[ f-icon-020 f-c-st-bla f-m-v-005 ]'
          />
        </CTA>
      )}
    </div>
  )
}

export default RequestWizard
